import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Alert,
  LinearProgress
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  Download,
  Upload,
  Refresh
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import ICDMasterForm from '../MedicalCoding/ICDMasterForm'
import api from '../../../services/api'

const ICDMasterManagement = () => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [icdCodes, setIcdCodes] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  
  // Filters
  const [search, setSearch] = useState('')
  const [icdVersion, setIcdVersion] = useState('')
  const [chapter, setChapter] = useState('')
  const [isActive, setIsActive] = useState('true')
  
  // Form state
  const [openForm, setOpenForm] = useState(false)
  const [editData, setEditData] = useState(null)

  useEffect(() => {
    loadICDCodes()
  }, [page, pageSize])

  const loadICDCodes = async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        limit: pageSize,
        search,
        icdVersion: icdVersion || undefined,
        chapter: chapter || undefined,
        isActive: isActive !== '' ? isActive : undefined
      }

      const response = await api.get('/master/icd', { params })
      
      if (response.data.success) {
        const data = response.data.data
        setIcdCodes(data.docs || data || [])
        setTotalRows(data.totalDocs || data.total || 0)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load ICD codes', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(0)
    loadICDCodes()
  }

  const handleReset = () => {
    setSearch('')
    setIcdVersion('')
    setChapter('')
    setIsActive('true')
    setPage(0)
    loadICDCodes()
  }

  const handleCreate = () => {
    setEditData(null)
    setOpenForm(true)
  }

  const handleEdit = (row) => {
    setEditData(row)
    setOpenForm(true)
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this ICD code?')) {
      try {
        const response = await api.delete(`/master/icd/${id}`)
        if (response.data.success) {
          enqueueSnackbar('ICD code deleted successfully', { variant: 'success' })
          loadICDCodes()
        }
      } catch (error) {
        enqueueSnackbar('Failed to delete ICD code', { variant: 'error' })
      }
    }
  }

  const handleFormSuccess = () => {
    loadICDCodes()
    setOpenForm(false)
    setEditData(null)
  }

  const columns = [
    {
      field: 'code',
      headerName: 'ICD Code',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
          {params.row.shortDescription && (
            <Typography variant="caption" color="text.secondary">
              {params.row.shortDescription}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'icdVersion',
      headerName: 'Version',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'ICD-10' ? 'primary' : 'secondary'}
        />
      )
    },
    {
      field: 'chapter',
      headerName: 'Chapter',
      width: 100
    },
    {
      field: 'block',
      headerName: 'Block',
      width: 120
    },
    {
      field: 'usageCount',
      headerName: 'Usage',
      width: 80,
      type: 'number'
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              sx={{ mr: 1 }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row._id)}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ]

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          ICD Code Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            sx={{ mr: 2 }}
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ mr: 2 }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Add ICD Code
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Search Code/Description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>ICD Version</InputLabel>
            <Select
              value={icdVersion}
              label="ICD Version"
              onChange={(e) => setIcdVersion(e.target.value)}
            >
              <MenuItem value="">All Versions</MenuItem>
              <MenuItem value="ICD-10">ICD-10</MenuItem>
              <MenuItem value="ICD-11">ICD-11</MenuItem>
              <MenuItem value="ICD-10-PCS">ICD-10-PCS</MenuItem>
              <MenuItem value="ICD-9-CM">ICD-9-CM</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Chapter"
            value={chapter}
            onChange={(e) => setChapter(e.target.value)}
            placeholder="e.g., IX"
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={isActive}
              label="Status"
              onChange={(e) => setIsActive(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<FilterList />}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <DataGrid
          rows={icdCodes}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={{
            page: page,
            pageSize: pageSize
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page)
            setPageSize(model.pageSize)
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClick
          getRowId={(row) => row._id}
          loading={loading}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            }
          }}
        />
      </Box>

      {/* Form Dialog */}
      <ICDMasterForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSuccess={handleFormSuccess}
        editData={editData}
      />
    </Box>
  )
}

export default ICDMasterManagement